import React from 'react';
import NextImage from 'next/image';
import { cn } from '@/lib/cn';

export interface ImageProps
  extends Omit<
    React.ComponentProps<typeof NextImage>,
    'src' | 'alt' | 'fill' | 'width' | 'height'
  > {
  aspectRatio?: '4:3' | '3:2' | '16:9' | '2:1' | '1:1';
  src: string;
  alt: string;
}

// Next.js image optimization fails for URLs that aren't conventional image
// files — proxy/thumbnail endpoints (.aspx?p=...), URLs with complex query
// strings, or anything served behind a third-party CDN that may return
// non-image content-types. Optimization errors out with a 400/500 and the
// browser shows a broken image, even though the URL itself is valid.
//
// Detection: if the pathname doesn't end in a standard image extension,
// pass `unoptimized` so <Image> serves the URL as-is. Internal CloudFront
// images (ending in .jpg/.png/.webp/etc.) keep using Next's optimizer for
// the size and format savings.
const STANDARD_IMAGE_EXT = /\.(jpe?g|png|webp|avif|gif)$/i;

function shouldSkipOptimization(src: string): boolean {
  if (!src) return false;
  // Internal Next-served paths (start with /) — always optimize.
  if (src.startsWith('/')) return false;
  try {
    const pathname = new URL(src).pathname;
    return !STANDARD_IMAGE_EXT.test(pathname);
  } catch {
    // Unparseable URL — safer to skip optimization than 500.
    return true;
  }
}

export function Image({
  aspectRatio = '4:3',
  src,
  alt,
  className,
  sizes = '(max-width: 380px) 80vw, 300px',
  quality = 75,
  unoptimized,
  ...props
}: ImageProps) {
  const aspectRatioClasses = {
    '4:3': 'aspect-[4/3]',
    '3:2': 'aspect-[3/2]',
    '16:9': 'aspect-video',
    '2:1': 'aspect-[2/1]',
    '1:1': 'aspect-square',
  } as const;

  // Caller can force unoptimized explicitly; otherwise auto-detect from URL.
  const skipOptimize = unoptimized ?? shouldSkipOptimization(src);

  return (
    <div className={cn('relative overflow-hidden', aspectRatioClasses[aspectRatio], className)}>
      <NextImage
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        quality={quality}
        unoptimized={skipOptimize}
        className="object-cover pointer-events-none"
        {...props}
      />
    </div>
  );
}
